import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import * as XLSX from 'xlsx';

import {
  Box,
  IconButton,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import PrimaryButton from '../../ui/buttons/primaryButton';
import { TableSortLabel } from '@mui/material';

import {
  addBrokerHouseCode,
  deleteBorkerHouseCode,
  getBrokerHouseCodes,
  updateBrokerHouseCode
} from '../../../features/brokerHouseCode/brokerHouseCode.slice';
import { getServiceProviders } from '../../../features/serviceProvider/serviceProvider.slice';
import PrimarySelect from '../../ui/forms/fields/select';
import Input from '../../ui/forms/fields/input';

const TableCell = styled(MuiTableCell)(({ theme }) => ({
  '&:last-child': {
    padding: theme.spacing(1)
  },
  borderBottom: 'none'
}));

const TableRow = styled(MuiTableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:first-of-type': {
    '& th': {
      borderBottom: `2px solid ${theme.palette.divider}`
    }
  }
}));

const schema = yup.object().shape({
  provider: yup.string().required('Manco / LISP Name is required'),
  code: yup.string().required('Broker House Code is required')
});

function HouseCodes() {
  const dispatch = useDispatch();
  const serviceProviders = useSelector((state) => state.serviceProvider.providers);
  const codes = useSelector((state) => state.brokerHouseCode.brokerCodes);
  const loadingCodes = useSelector((state) => state.brokerHouseCode.loading);

  const [selectedCode, setSelectedCode] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const [progress, setProgress] = React.useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'provider', direction: 'asc' });
  const [file, setFile] = useState(null);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedProviders = [...(codes || [])].sort((a, b) => {
    const valueA = a[sortConfig.key]?.toLowerCase?.() || '';
    const valueB = b[sortConfig.key]?.toLowerCase?.() || '';

    if (valueA < valueB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),

    mode: 'onBlur'
  });

  useEffect(() => {
    dispatch(getServiceProviders());
    dispatch(getBrokerHouseCodes());
  }, [dispatch]);

  useEffect(() => {
    const selectedCodeisEmpty = Object.keys(selectedCode).length === 0;

    if (!selectedCodeisEmpty) {
      setValue('provider', selectedCode.provider, { shouldValidate: true });
      setValue('code', selectedCode.code, { shouldValidate: true });
    }
  }, [selectedCode, setValue]);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 100);
      });
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const handleEdit = (data) => {
    setSelectedCode({ ...data });
    setDialogOpen(true);
  };

  const handleAdd = () => {
    reset();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedCode({});
    setDialogOpen(false);
  };

  const handleDeleteCode = (id) => {
    setCodeToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCode = () => {
    dispatch(deleteBorkerHouseCode(codeToDelete))
      .unwrap()
      .then(() => {
        dispatch(getBrokerHouseCodes());
      });
    setDeleteDialogOpen(false);
    setCodeToDelete(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      parseExcel(file);
    }
  };

  const findServiceProvider = (providers, searchString) => {
    if (!Array.isArray(providers)) {
      throw new Error('Invalid input: "providers" must be an array.');
    }
    if (typeof searchString !== 'string' || !searchString.trim()) {
      throw new Error('Invalid input: "searchString" must be a non-empty string.');
    }

    const result = providers.find((provider) => provider.name === searchString);

    if (result) {
      return {
        serviceProvider: result._id?.$oid || result._id,
        provider: result.name
      };
    }

    return null;
  };

  const parseExcel = (file) => {
    if (!file) {
      console.error('No file provided for parsing.');
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const binaryString = e.target.result;
        const workbook = XLSX.read(binaryString, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];

        if (!sheetName) {
          console.error('No sheets found in the workbook.');
          return;
        }

        const sheet = workbook.Sheets[sheetName];
        const codeData = XLSX.utils.sheet_to_json(sheet);

        const generateUniqueId = (index) => `${Date.now()}_${index}`;

        const newCode = codeData.map((code, index) => {
          const providerInfo = findServiceProvider(serviceProviders, code['MANCO / LISP Name']);

          return {
            id: generateUniqueId(index),
            serviceProvider: providerInfo?.serviceProvider || '',
            provider: providerInfo?.provider || '',
            code: code['Broker House Code'] || ''
          };
        });

        newCode.forEach((code) => {
          if (code.serviceProvider && code.code) {
            dispatch(addBrokerHouseCode(code))
              .unwrap()
              .then(() => {
                dispatch(getBrokerHouseCodes());
              });
          } else {
            console.warn('Invalid code data:', code);
          }
        });
      } catch (error) {
        console.error('Error parsing Excel file:', error);
      }
    };

    reader.onerror = (error) => {
      console.error('File reading error:', error);
    };

    reader.readAsBinaryString(file);
  };
  const onSubmit = (data) => {
    const foundProvider = serviceProviders.find((provider) => provider.name === data.provider);

    if (foundProvider) {
      data.serviceProvider = foundProvider._id;
      if (Object.keys(selectedCode).length !== 0) {
        data._id = selectedCode._id;
      }

      if (Object.keys(selectedCode).length === 0) {
        dispatch(addBrokerHouseCode(data))
          .unwrap()
          .then(() => {
            dispatch(getBrokerHouseCodes());
          });
      } else {
        dispatch(updateBrokerHouseCode(data))
          .unwrap()
          .then(() => {
            dispatch(getBrokerHouseCodes());
          });
      }

      handleCloseDialog();
    } else {
      console.log('Provider not found');
    }
  };

  const downloadUrl =
    'https://s3.af-south-1.amazonaws.com/s3.fairtree.public/portal/House%2BCodes%2BTemplate.xlsx';

  return (
    <Box p={3}>
      <TableContainer component={Paper} sx={{ padding: 3, borderRadius: 5 }}>
        <Box mb={3}>
          <Typography variant="h2" fontWeight={500} gutterBottom>
            House Codes
          </Typography>
          <Typography variant="subtitle1" fontSize={'20px'}>
            Add the names of the MANCO’s / LISP’s and the respective Broker House Codes to link to:
          </Typography>
        </Box>

        <Box alignItems={'center'} display="flex" justifyContent="flex-end" gap={2} mb={2}>
          <PrimaryButton
            href={downloadUrl}
            sx={{
              minWidth: '200px',
              borderRadius: 2,
              height: '40px',
              backgroundColor: '#000000',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#333'
              }
            }}
            onClick={() => {
              setLoading(true);
              setTimeout(() => {
                setLoading(false);
              }, 3000);
            }}>
            {loading ? 'Downloading Template...' : 'DOWNLOAD TEMPLATE'}
          </PrimaryButton>
          <Button
            variant="contained"
            component="label"
            sx={{
              width: '200px',
              borderRadius: 2,
              height: '40px',
              backgroundColor: '#CC6323',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#9A4D19'
              }
            }}>
            UPLOAD TEMPLATE
            <input type="file" accept=".xlsx" hidden onChange={handleFileChange} />
          </Button>
          <IconButton color="primary" onClick={() => handleAdd()}>
            <AddCircleOutlineIcon sx={{ fontSize: 40 }} />
          </IconButton>
        </Box>

        {loadingCodes && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              width: '100%',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#CC6323'
              },
              backgroundColor: '#F1A46E'
            }}
          />
        )}

        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={sortConfig.key === 'provider' ? sortConfig.direction : false}>
                <TableSortLabel
                  active={sortConfig.key === 'provider'}
                  direction={sortConfig.key === 'provider' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('provider')}>
                  Manco / LISP Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Broker House Code</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedProviders.map((code, index) => (
              <TableRow key={index}>
                <TableCell>{code.provider}</TableCell>
                <TableCell>{code.code}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(code)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteCode(code._id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        sx={{ padding: 3, borderRadius: '50px' }}
        fullWidth>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500]
            })}>
            <CloseIcon sx={{ fontSize: 30 }} />
          </IconButton>
          <DialogContent sx={{ mt: 5 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box mb={2}>
                <PrimarySelect
                  {...register('provider')}
                  control={control}
                  margin="dense"
                  label="Manco / LISP Name"
                  fullWidth
                  error={!!errors.provider}
                  helperText={errors.provider?.message}>
                  {serviceProviders?.map((provider, index) => (
                    <MenuItem key={index} value={provider.name}>
                      {provider.name}
                    </MenuItem>
                  ))}
                </PrimarySelect>
              </Box>
              <Box mb={2}>
                <Input
                  label="Broker House Code"
                  {...register('code')}
                  control={control}
                  error={!!errors.code}
                  helperText={errors.code?.message}
                />
              </Box>

              <DialogActions>
                <Button variant="contained" type="submit">
                  Save
                </Button>
              </DialogActions>
            </form>
          </DialogContent>
        </Box>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this house code? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button sx={{ color: '#CC6323' }} onClick={confirmDeleteCode}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HouseCodes;
